import { localization } from "@/globals";
import { useBookingStore } from "@/store/booking";
import { MemberDiscountType } from "@/store/booking/types";
import { useFlowStore } from "@/store/flow";
import { useInitialSetupStore } from "@/store/initialSetup";
import { BookingRoutePaths, RouteNames } from "@/utils/constants";
import { NavigationGuardNext, RouteRecordRaw, RouterView } from "vue-router";

export const routes: Array<RouteRecordRaw> = [
  {
    path: "/sign-in",
    name: RouteNames.SignIn,
    component: () => import("pages/home/sign-in.vue")
  },
  {
    path: "/sign-in/callback",
    name: RouteNames.SignInCallback,
    component: () => import("pages/home/sign-in-callback.vue")
  },
  {
    path: "/",
    component: () => import("pages/home.vue"),
    children: [
      // This is for /
      {
        path: "",
        name: RouteNames.Home,
        component: () => import("organisms/dashboard/home/home.vue")
      },
      {
        path: "reports",
        name: RouteNames.DashboardReports,
        component: () =>
          import("@/components/organisms/dashboard/reports/reports-page.vue")
      },
      {
        path: "documents",
        name: RouteNames.DashboardDocuments,
        redirect: { name: RouteNames.DashboardReports }
      },
      {
        path: "invoices",
        name: RouteNames.DashboardInvoices,
        component: () =>
          import("@/components/organisms/dashboard/invoices/invoices-page.vue")
      },
      {
        path: "reports/digital-report/:encounterId",
        name: RouteNames.DigitalReport,
        component: () => import("pages/reports/digital-report-page.vue"),
        props: true
      },
      {
        path: "referrals",
        name: RouteNames.DashboardReferrals,
        component: () => import("organisms/dashboard/referrals/referrals.vue"),
        beforeEnter: (to, from, next) => {
          if (localization === "US") {
            next({ name: RouteNames.Home });
          }

          next();
        }
      },
      {
        path: "account",
        name: RouteNames.DashboardAccount,
        component: () => import("organisms/dashboard/account/account.vue")
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/login/renewal",
    name: RouteNames.LoginRenewal,
    component: () => import("pages/home/sign-in.vue")
  },
  {
    path: "/login/medical-questionnaire",
    name: RouteNames.LoginMedicalQuestionnaire,
    component: () => import("pages/home/sign-in.vue"),
    props: route => ({
      encounterId: route.query.encounterId
    })
  },
  {
    path: "/login/lmp-update",
    name: RouteNames.LoginLmpUpdate,
    component: () => import("pages/home/sign-in.vue"),
    props: route => ({
      encounterId: route.query.encounterId
    })
  },
  {
    path: "/login/obo",
    name: RouteNames.LoginOBO,
    component: () => import("pages/home/sign-in-obo.vue")
  },
  {
    path: "/sign-in/callback/login/obo",
    name: RouteNames.SignInCallbackOBO,
    component: () => import("pages/home/sign-in-callback-obo.vue")
  },
  {
    path: "/sign-in/callback/function",
    name: RouteNames.SignInCallbackFunction,
    component: () => import("pages/home/sign-in-callback-function.vue")
  },
  {
    path: "/sign-out/callback",
    component: () => import("pages/home/sign-out-callback.vue")
  },
  {
    path: "/join",
    name: RouteNames.Join,
    component: () => import("pages/join.vue"),
    props: true
  },
  {
    path: "/mq/confirm",
    component: () => import("pages/mq/confirm.vue"),
    props: route => ({
      token: route.query.token,
      value: route.query.value
    }),
    meta: { requiresAuth: false }
  },
  {
    path: "/link-association",
    name: RouteNames.LinkAssociation,
    component: () => import("pages/link-association.vue"),
    props: route => ({
      partnerName: route.query.partnerName,
      offerType: route.query.offerType
    }),
    beforeEnter: (to, from, next) => {
      partnerAssociateRouteGuard(next);
    }
  },
  {
    path: "/forgot-password",
    name: RouteNames.ForgotPassword,
    component: () => import("pages/forgot-password/forgot-password.vue")
  },
  {
    path: "/forgot-password/change",
    name: RouteNames.ForgotPasswordChange,
    component: () => import("pages/forgot-password/forgot-password-change.vue"),
    props: route => ({ email: route.query.email, code: route.query.code })
  },
  {
    path: "/forgot-password/confirm",
    name: RouteNames.ForgotPasswordConfirm,
    component: () => import("pages/forgot-password/forgot-password-confirm.vue")
  },
  {
    path: "/cat/results",
    name: RouteNames.CATResults,
    component: () => import("pages/cat/results.vue")
  },
  {
    path: "/cat/results/sign-up",
    name: RouteNames.CATResultsSignUp,
    component: () => import("pages/cat/results-sign-up.vue")
  },
  {
    path: "/cat/questionnaire",
    name: RouteNames.CATQuestionnaire,
    component: () => import("pages/cat/cat-flow-page.vue")
  },
  {
    path: "/medical-questionnaire",
    name: RouteNames.Flow,
    component: () => import("@/components/pages/mq/flow-page.vue"),
    meta: {
      requiresAuth: true
    },
    beforeEnter: (to, from, next) => {
      if (
        !to.query.extraData ||
        !(JSON.parse(to.query.extraData as string) as any)?.encounterId
      ) {
        next("/");
      } else if (to.query.direct || useFlowStore().active) {
        next();
      } else {
        next("/");
      }
    }
  },
  {
    path: "/mammo-upload/:encounterId",
    name: RouteNames.MammoFileUpload,
    component: () => import("organisms/sign-up/mammo-upload.vue"),
    props: true,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/schedule-actions/:encounterId/:appointmentId?/:appointmentCategory?/:isSelectMoreTimes?",
    name: RouteNames.ScheduleAction,
    component: () => import("pages/schedule-actions/schedule-action-page.vue"),
    props: route => ({
      encounterId: route.params.encounterId,
      appointmentId: route.params.appointmentId,
      appointmentCategory: route.params.appointmentCategory,
      isSelectMoreTimes: route.params.isSelectMoreTimes
    }),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/initial-setup/tos",
    name: RouteNames.InitialSetupTOS,
    component: () => import("pages/initial-setup/tos.vue"),
    props: route => {
      const initialSetupStore = useInitialSetupStore();
      const { email, code, hasPassword } = route.query;

      // If we have query params, set up the store
      initialSetupStore.setupData = {
        email: email as string,
        code: code as string,
        hasPassword: hasPassword === "true"
      };

      return {
        email: initialSetupStore.setupData?.email,
        code: initialSetupStore.setupData?.code,
        hasPassword: initialSetupStore.setupData?.hasPassword
      };
    }
  },
  {
    path: "/initial-setup/set-password",
    name: RouteNames.InitialSetupSetPassword,
    component: () => import("pages/initial-setup/set-password.vue"),
    props: () => {
      const initialSetupStore = useInitialSetupStore();
      return {
        email: initialSetupStore.setupData.email,
        code: initialSetupStore.setupData.code,
        hasPassword: initialSetupStore.setupData.hasPassword,
        optInEmailNotifications:
          initialSetupStore.setupData.optInEmailNotifications,
        optInPhoneNotifications:
          initialSetupStore.setupData.optInPhoneNotifications
      };
    },
    beforeEnter: (to, from, next) => {
      const initialSetupStore = useInitialSetupStore();

      // If we don't have setup data in the store, redirect to Home
      if (
        !initialSetupStore.setupData.email ||
        !initialSetupStore.setupData.code
      ) {
        next({ name: RouteNames.Home });
        return;
      }

      next();
    }
  },
  {
    path: "/sign-up/referral",
    name: RouteNames.SignUpReferral,
    component: () => import("organisms/sign-up/member-offer-landing.vue"),
    props: route => ({
      code: route.query.code,
      memberDiscountType: MemberDiscountType.REFERRAL
    }),
    meta: {
      requiresAuth: false
    },
    beforeEnter: (to, from, next) => {
      if (localization === "US") {
        next({ name: RouteNames.Home });
      }

      next();
    }
  },
  {
    path: "/sign-up/claim-offer",
    name: RouteNames.SignUpClaimOffer,
    component: () => import("organisms/sign-up/member-offer-landing.vue"),
    props: route => ({
      code: route.query.code,
      partner: route.query.partner,
      memberDiscountType: MemberDiscountType.INVITED
    }),
    meta: {
      requiresAuth: false
    }
  },
  {
    path: "/sign-up/claim-offer-sponsored",
    name: RouteNames.SignUpClaimOfferSponsored,
    component: () => import("organisms/sign-up/member-offer-landing.vue"),
    props: route => ({
      code: route.query.code,
      partner: route.query.partner,
      encounterName: route.query.encounterName,
      memberDiscountType: MemberDiscountType.SPONSORED
    }),
    meta: {
      requiresAuth: false
    }
  },
  {
    path: "/payment-authorize/:encounterId",
    name: RouteNames.PaymentAuthorize,
    component: () =>
      import("pages/authorize-payment/authorize-payment-page.vue"),
    props: true,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/lmp-update/:encounterId",
    name: RouteNames.LmpUpdate,
    component: () => import("pages/lmp-update/lmp-update-page.vue"),
    props: true,
    meta: {
      requiresAuth: true
    }
  },
  signupRoutes(BookingRoutePaths.SignUp),
  signupRoutes(BookingRoutePaths.Invite),
  signupRoutes(BookingRoutePaths.Sponsored),
  signupRoutes(BookingRoutePaths.BookScan),
  signupRoutes(BookingRoutePaths.Renewal),
  signupRoutes(BookingRoutePaths.Referral),
  // For any unmatched paths redirect home (prevents white screen for non-existing paths)
  {
    path: "/:pathMatch(.*)*",
    redirect: { name: RouteNames.Home, params: {} }
  }
];

function signupRoutes(basePath: string): RouteRecordRaw {
  return {
    path: `/${basePath}`,
    meta: {
      requiresAuth: true
    },
    beforeEnter: (to, from, next) => {
      // There are no valid paths for these routes on it's own without select plan, schedule scan and etc..
      if (to.path === `/${basePath}`) {
        next({ name: RouteNames.Home });
      }

      next();
    },
    component: RouterView,
    children: [
      {
        path: "select-plan",
        name: `${basePath}/${RouteNames.BookingSelectPlan}`,
        component: () => import("organisms/sign-up/select-plan-page.vue"),
        props: true
      },
      {
        path: "schedule-scan",
        name: `${basePath}/${RouteNames.BookingScheduleScan}`,
        component: () => import("organisms/sign-up/schedule-scan.vue"),
        props: true,
        beforeEnter: (to, from, next) => {
          signupRouteGuard(next);
        }
      },
      {
        path: "change-location",
        name: `${basePath}/${RouteNames.BookingChangeLocationSchedule}`,
        component: () =>
          import("organisms/sign-up/schedule-change-location.vue"),
        props: true,
        beforeEnter: (to, from, next) => {
          signupRouteGuard(next);
        }
      },
      {
        path: "reserve-appointment",
        name: `${basePath}/${RouteNames.BookingReserveAppointment}`,
        component: () => import("organisms/sign-up/reserve-appointment.vue"),
        props: true,
        beforeEnter: (to, from, next) => {
          signupRouteGuard(next);
        }
      },
      {
        path: "scan-confirm",
        name: `${basePath}/${RouteNames.BookingScanConfirmation}`,
        component: () => import("organisms/sign-up/scan-confirmation.vue"),
        props: true,
        beforeEnter: (to, from, next) => {
          signupRouteGuard(next);
        }
      },
      {
        path: "booking-error",
        name: `${basePath}/${RouteNames.BookingError}`,
        component: () => import("organisms/sign-up/booking-error.vue"),
        props: true,
        beforeEnter: (to, from, next) => {
          signupRouteGuard(next);
        }
      }
    ]
  };
}

function signupRouteGuard(next: NavigationGuardNext) {
  if (useBookingStore().validEntry) {
    next();
  } else {
    next("/");
  }
}

function partnerAssociateRouteGuard(next: NavigationGuardNext) {
  if (useBookingStore().validAssociateEntry) {
    next();
  } else {
    next("/");
  }
}
